import { useYearPeriodStore } from '../../store/useYearPeriodStore'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { YearPeriodPicker } from './shared/YearPeriodPicker'
import { MlyGrid } from './shared/MlyGrid'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { withShortLabels } from './shared/rowGroupsTransposed'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useMonthlyData } from '../../hooks/useMonthlyData'
import { useMonthlyNormals } from '../../hooks/useMonthlyNormals'

const SLUG = 'mly-pudni'

const ROW_GROUPS = [
  {
    label: '5 cm',
    rows: [
      { tsId: 'SOIL05AVG_AVG', label: 'Průměrná teplota půdy 5 cm', unit: '°C', normalMode: null, decimals: 1 },
      { tsId: 'SOIL05AVG_MAX', label: 'Maximum 5 cm',                unit: '°C', normalMode: null, decimals: 1 },
      { tsId: 'SOIL05AVG_MIN', label: 'Minimum 5 cm',                unit: '°C', normalMode: null, decimals: 1 },
    ],
  },
  {
    label: '10 cm',
    rows: [
      { tsId: 'SOIL10AVG_AVG', label: 'Průměrná teplota půdy 10 cm', unit: '°C', normalMode: null, decimals: 1 },
      { tsId: 'SOIL10AVG_MAX', label: 'Maximum 10 cm',                unit: '°C', normalMode: null, decimals: 1 },
      { tsId: 'SOIL10AVG_MIN', label: 'Minimum 10 cm',                unit: '°C', normalMode: null, decimals: 1 },
    ],
  },
  {
    label: '20 cm',
    rows: [
      { tsId: 'SOIL20AVG_AVG', label: 'Průměrná teplota půdy 20 cm', unit: '°C', normalMode: null, decimals: 1 },
      { tsId: 'SOIL20AVG_MAX', label: 'Maximum 20 cm',                unit: '°C', normalMode: null, decimals: 1 },
      { tsId: 'SOIL20AVG_MIN', label: 'Minimum 20 cm',                unit: '°C', normalMode: null, decimals: 1 },
    ],
  },
  {
    label: '50 cm',
    rows: [
      { tsId: 'SOIL50AVG_AVG', label: 'Průměrná teplota půdy 50 cm', unit: '°C', normalMode: null, decimals: 1 },
      { tsId: 'SOIL50AVG_MAX', label: 'Maximum 50 cm',                unit: '°C', normalMode: null, decimals: 1 },
      { tsId: 'SOIL50AVG_MIN', label: 'Minimum 50 cm',                unit: '°C', normalMode: null, decimals: 1 },
    ],
  },
  {
    label: '100 cm',
    rows: [
      { tsId: 'SOIL100AVG_AVG', label: 'Průměrná teplota půdy 100 cm', unit: '°C', normalMode: null, decimals: 1 },
      { tsId: 'SOIL100AVG_MAX', label: 'Maximum 100 cm',                unit: '°C', normalMode: null, decimals: 1 },
      { tsId: 'SOIL100AVG_MIN', label: 'Minimum 100 cm',                unit: '°C', normalMode: null, decimals: 1 },
    ],
  },
]

// 2. definice hlavičky – transponovaná tabulka. Hloubka je ve skupině → řádek jen průměr/max/min.
const ROW_GROUPS_T = withShortLabels(ROW_GROUPS, {
  SOIL05AVG_AVG: 'Průměr', SOIL05AVG_MAX: 'Maximum', SOIL05AVG_MIN: 'Minimum',
  SOIL10AVG_AVG: 'Průměr', SOIL10AVG_MAX: 'Maximum', SOIL10AVG_MIN: 'Minimum',
  SOIL20AVG_AVG: 'Průměr', SOIL20AVG_MAX: 'Maximum', SOIL20AVG_MIN: 'Minimum',
  SOIL50AVG_AVG: 'Průměr', SOIL50AVG_MAX: 'Maximum', SOIL50AVG_MIN: 'Minimum',
  SOIL100AVG_AVG: 'Průměr', SOIL100AVG_MAX: 'Maximum', SOIL100AVG_MIN: 'Minimum',
})

const TS_IDS = ROW_GROUPS.flatMap(g => g.rows.map(r => r.tsId))

export function MlyPudniView({ station }) {
  const { year, setYear } = useYearPeriodStore()
  const orientation = useTableOrientationStore((s) => s.getOrientation(SLUG))

  const { dataMap, yearMap, dtMap, yearDtMap, isLoading: dLoading, isError: dError } = useMonthlyData(station?.gh_id, year, TS_IDS)
  const { normalsMap, yearNormalsMap, isLoading: nLoading }        = useMonthlyNormals(station?.gh_id, TS_IDS)

  const isLoading = dLoading || nLoading

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}
        <div className="flex items-center justify-between gap-3">
          <YearPeriodPicker year={year} onYearChange={setYear} obsType="DLY" />
          <TableOrientationToggle slug={SLUG} />
        </div>
      </div>
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {isLoading && <div className="flex items-center gap-2 text-sm text-gray-500"><Spinner size="sm" /> Načítání dat…</div>}
        {dError && <p className="text-red-500 text-sm">Chyba při načítání dat</p>}
        {!isLoading && !dError && (
          <MlyGrid
            rowGroups={ROW_GROUPS}
            rowGroupsTransposed={ROW_GROUPS_T}
            orientation={orientation}
            dataMap={dataMap}
            yearMap={yearMap}
            dtMap={dtMap}
            yearDtMap={yearDtMap}
            normalsMap={normalsMap}
            yearNormalsMap={yearNormalsMap}
          />
        )}
      </div>
    </div>
  )
}
